import createClient, { type Client } from "openapi-fetch";
import type { paths as HkPaths } from "../schema/hk.js";
import type { paths as SgPaths } from "../schema/sg.js";
import { ApiError, NetworkError, type ProblemDetails } from "../recovery/index.js";

/**
 * Per-region API client (CAP-6) and the in-memory web session (CAP-3).
 *
 * - Types come from each region's OpenAPI schema (`sync-schema:sg` / `:hk`).
 * - Every request sends `X-KN-Client: web` (opts into the refresh cookie and
 *   is the CSRF guard) and the stable per-browser `device` id.
 * - The access token lives in memory only. The refresh token is an HttpOnly
 *   cookie the browser sends with `credentials: "include"`.
 * - A 401 triggers one cookie refresh (single-flight) and one retry; if that
 *   fails the session is cleared and `onSessionExpired` fires.
 */

export type Region = "sg" | "hk";
export type { SgPaths, HkPaths };

export const WEB_CLIENT_HEADER = "X-KN-Client";
export const DEVICE_STORAGE_KEY = "kn.device";

// ---------------------------------------------------------------- session

export interface SessionUser {
  id: string;
  email: string;
  name?: string;
}

export interface SessionStore {
  getAccessToken(): string | null;
  getUser(): SessionUser | null;
  isAuthenticated(): boolean;
  setSession(token: string, user?: SessionUser | null): void;
  setAccessToken(token: string): void;
  clear(): void;
  subscribe(cb: () => void): () => void;
}

export function createSessionStore(): SessionStore {
  let token: string | null = null;
  let user: SessionUser | null = null;
  const subs = new Set<() => void>();
  const emit = () => {
    for (const s of subs) s();
  };
  return {
    getAccessToken: () => token,
    getUser: () => user,
    isAuthenticated: () => token !== null,
    setSession(t, u) {
      token = t;
      if (u !== undefined) user = u;
      emit();
    },
    setAccessToken(t) {
      token = t;
      emit();
    },
    clear() {
      token = null;
      user = null;
      emit();
    },
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
  };
}

// ---------------------------------------------------------------- device id

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Stable random id for this browser (not a secret). The backend keys the web
 * refresh token by (user, device).
 */
export function getOrCreateDeviceId(
  storage: KeyValueStorage | null,
  random: () => string = () => globalThis.crypto.randomUUID(),
): string {
  try {
    const existing = storage?.getItem(DEVICE_STORAGE_KEY);
    if (existing) return existing;
    const id = `web-${random()}`;
    storage?.setItem(DEVICE_STORAGE_KEY, id);
    return id;
  } catch {
    return `web-${random()}`;
  }
}

// ---------------------------------------------------------------- fetch

export interface KnFetchOptions {
  region: Region;
  apiBaseUrl: string;
  session: SessionStore;
  deviceId: string;
  fetch?: typeof fetch;
  /** Called after a 401 that a cookie refresh could not fix. */
  onSessionExpired?: () => void;
  /** Called for every response (2xx) — freshness tracking. */
  onSuccess?: () => void;
  /** Per-request timeout. */
  timeoutMs?: number;
}

function stripSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

export function regionApiPrefix(apiBaseUrl: string, region: Region): string {
  return `${stripSlash(apiBaseUrl)}/api/${region}`;
}

const NO_REFRESH_SUFFIXES = ["/users/accessToken", "/users/login", "/users/loginWithOtp", "/users/signupWithOtp"];

async function readProblem(res: Response): Promise<ProblemDetails | undefined> {
  try {
    const text = await res.clone().text();
    return text ? (JSON.parse(text) as ProblemDetails) : undefined;
  } catch {
    return undefined;
  }
}

export interface KnFetch {
  fetch: (input: Request) => Promise<Response>;
  /** Cookie refresh; true when a new access token is in the session. */
  refresh: () => Promise<boolean>;
}

interface Outgoing {
  url: string;
  method: string;
  headers: Headers;
  body?: ArrayBuffer;
}

async function snapshot(input: Request, rewriteUrl?: (url: string) => string): Promise<Outgoing> {
  const method = input.method.toUpperCase();
  const body = method === "GET" || method === "HEAD" ? undefined : await input.arrayBuffer();
  return {
    url: rewriteUrl ? rewriteUrl(input.url) : input.url,
    method,
    headers: new Headers(input.headers),
    body: body && body.byteLength > 0 ? body : undefined,
  };
}

export function createKnFetch(
  opts: KnFetchOptions & { rewriteUrl?: (url: string) => string },
): KnFetch {
  const baseFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const prefix = regionApiPrefix(opts.apiBaseUrl, opts.region);
  let inflight: Promise<boolean> | null = null;

  const send = async (out: Outgoing, withAuth: boolean): Promise<Response> => {
    const headers = new Headers(out.headers);
    headers.set(WEB_CLIENT_HEADER, "web");
    headers.set("device", opts.deviceId);
    const token = opts.session.getAccessToken();
    if (withAuth && token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), opts.timeoutMs ?? 30_000);
    try {
      return await baseFetch(
        new Request(out.url, {
          method: out.method,
          headers,
          body: out.body,
          credentials: "include",
          signal: controller.signal,
        }),
      );
    } catch (err) {
      throw new NetworkError(err, controller.signal.aborted);
    } finally {
      clearTimeout(timer);
    }
  };

  const refresh = (): Promise<boolean> => {
    inflight ??= (async () => {
      try {
        const res = await send(
          {
            url: `${prefix}/users/accessToken`,
            method: "PUT",
            headers: new Headers({ "Content-Type": "application/json" }),
            body: new TextEncoder().encode("{}").buffer as ArrayBuffer,
          },
          false,
        );
        if (!res.ok) return false;
        const body = (await res.json()) as { data?: { accessToken?: string } };
        const token = body.data?.accessToken;
        if (!token) return false;
        opts.session.setAccessToken(token);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => {
          inflight = null;
        }, 0);
      }
    })();
    return inflight;
  };

  const knFetch = async (input: Request): Promise<Response> => {
    const out = await snapshot(input, opts.rewriteUrl);
    const res = await send(out, true);
    const path = new URL(out.url).pathname;
    const skipRefresh = NO_REFRESH_SUFFIXES.some((s) => path.endsWith(s));
    if (res.status === 401 && !skipRefresh) {
      const hadSession = opts.session.isAuthenticated();
      if (await refresh()) {
        const retried = await send(out, true);
        if (retried.ok) opts.onSuccess?.();
        if (retried.status === 401) {
          opts.session.clear();
          opts.onSessionExpired?.();
        }
        return retried;
      }
      opts.session.clear();
      if (hadSession) opts.onSessionExpired?.();
      return res;
    }
    if (res.ok) opts.onSuccess?.();
    return res;
  };

  return { fetch: knFetch, refresh };
}

// ---------------------------------------------------------------- clients

export type RegionClient =
  | { region: "sg"; client: Client<SgPaths> }
  | { region: "hk"; client: Client<HkPaths> };

export interface Api {
  region: Region;
  regionClient: RegionClient;
  /**
   * The region's client typed against the SG schema with paths rewritten to
   * `/api/{region}/…`. Shared routes (users, health) have identical shapes in
   * both regions (asserted in `region_paths.ts`). Region-specific journeys use
   * `regionClient` directly.
   */
  shared: Client<SgPaths>;
  refresh: () => Promise<boolean>;
}

export type CreateApiOptions = KnFetchOptions;

export function createApi(opts: CreateApiOptions): Api {
  const baseUrl = stripSlash(opts.apiBaseUrl);
  // One fetch (one refresh single-flight) for both clients. Non-SG regions
  // rewrite the shared client's `/api/sg/` paths to their own prefix; the
  // region's own client already uses `/api/{region}/` and is untouched.
  const kn = createKnFetch({
    ...opts,
    rewriteUrl:
      opts.region === "sg"
        ? undefined
        : (url) => url.replace(`${baseUrl}/api/sg/`, `${baseUrl}/api/${opts.region}/`),
  });
  const regionClient: RegionClient =
    opts.region === "sg"
      ? { region: "sg", client: createClient<SgPaths>({ baseUrl, fetch: kn.fetch }) }
      : { region: "hk", client: createClient<HkPaths>({ baseUrl, fetch: kn.fetch }) };
  const shared = createClient<SgPaths>({ baseUrl, fetch: kn.fetch });
  return { region: opts.region, regionClient, shared, refresh: kn.refresh };
}

/** openapi-fetch result → data, or throw ApiError with the problem body. */
export async function unwrap<T>(
  result: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await result;
  if (!response.ok || error !== undefined) {
    const problem =
      error && typeof error === "object" ? (error as ProblemDetails) : await readProblem(response);
    throw new ApiError(response.status, problem, response.headers.get("X-Request-ID") ?? undefined);
  }
  return data as T;
}
